import { getFCMToken, onMessageListener, deleteFCMToken } from 'utils/firebase';
import BaseService from 'services/base';
import { IDefinition } from 'types/utils/graphql';
import { FCM_MUTATIONS, FCM_RESPONSE_PATHS, DEVICE_PLATFORM, DEVICE_STATUS } from './graphql';

// DeviceRegistration interface removed as we're using direct GraphQL mutations

class FCMService extends BaseService {
  static DEFINITION: IDefinition = {
    name: 'device'
  };

  private fcmToken: string | null = null;
  private tokenRefreshTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<boolean> {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return false;
      }

      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if notification permission is granted
   */
  checkPermission(): boolean {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  }

  /**
   * Get FCM token and sync with backend
   */
  async getToken(): Promise<string | null> {
    try {
      // Check if we have permission first
      const hasPermission = this.checkPermission();
      if (!hasPermission) {
        return null;
      }

      // Get FCM token from Firebase
      const token = await getFCMToken();

      if (token && token !== this.fcmToken) {
        this.fcmToken = token;
        // Sync with backend
        await this.syncTokenWithBackend(token);
      }

      return token;
    } catch (error) {
      return null;
    }
  }

  /**
   * Register device with backend
   */
  async syncTokenWithBackend(token: string): Promise<void> {
    try {
      // Get OS version info (navigator.platform is deprecated)
      const getOSVersion = (): string => {
        // Use userAgentData if available (with type casting)
        const nav = navigator as any;
        if (nav.userAgentData && nav.userAgentData.platform) {
          return nav.userAgentData.platform;
        }
        // Fallback to parsing userAgent
        const userAgent = navigator.userAgent;
        if (userAgent.includes('Windows')) return 'Windows';
        if (userAgent.includes('Mac')) return 'macOS';
        if (userAgent.includes('Linux')) return 'Linux';
        if (userAgent.includes('Android')) return 'Android';
        if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
        return 'Unknown';
      };

      // Prepare device information
      const deviceInfo = {
        token: token,
        platform: DEVICE_PLATFORM.WEB,
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
        osVersion: getOSVersion()
      };

      // Register device with backend
      const response = await this.custom(
        {
          typeQL: "mutation",
          PATH_RESPONSE: FCM_RESPONSE_PATHS.REGISTER_DEVICE,
          variables: deviceInfo
        },
        FCM_MUTATIONS.REGISTER_DEVICE
      );

      if (response?.data) {
        this.isInitialized = true;
      } else if (response?.errors) {
        // Silent failure - device registration errors
      }
    } catch (error) {
      // Don't throw - we still want to use local notifications even if backend fails
    }
  }

  /**
   * Delete/unregister device token
   */
  async deleteToken(): Promise<void> {
    if (!this.fcmToken) {
      return;
    }

    try {
      // Unregister from backend
      const response = await this.custom(
        {
          typeQL: "mutation",
          PATH_RESPONSE: FCM_RESPONSE_PATHS.UNREGISTER_DEVICE,
          variables: {
            token: this.fcmToken
          }
        },
        FCM_MUTATIONS.UNREGISTER_DEVICE
      );

      if (response?.data) {
        // Device unregistered successfully
      }

      // Delete local FCM token
      await deleteFCMToken();

      this.fcmToken = null;
      this.isInitialized = false;
      this.stopTokenRefresh();
    } catch (error) {
      // Silent failure - failed to delete device token
    }
  }

  /**
   * Update device status
   */
  async updateDeviceStatus(status: 'ACTIVE' | 'INACTIVE'): Promise<void> {
    if (!this.fcmToken) {
      return;
    }

    try {
      const response = await this.custom(
        {
          typeQL: "mutation",
          PATH_RESPONSE: FCM_RESPONSE_PATHS.UPDATE_DEVICE_STATUS,
          variables: {
            token: this.fcmToken,
            status
          }
        },
        FCM_MUTATIONS.UPDATE_DEVICE_STATUS
      );

      if (response?.data) {
        // Device status updated successfully
      }
    } catch (error) {
      // Silent failure - failed to update device status
    }
  }

  /**
   * Start automatic token refresh
   */
  startTokenRefresh(): void {
    // Stop any existing timer
    this.stopTokenRefresh();

    // Refresh token every 7 days
    this.tokenRefreshTimer = setInterval(async () => {
      await this.getToken();
    }, 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
  }

  /**
   * Stop automatic token refresh
   */
  stopTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Initialize FCM service
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    try {
      const hasPermission = this.checkPermission();
      if (hasPermission) {
        const token = await this.getToken();
        if (token) {
          this.startTokenRefresh();
          this.isInitialized = true;
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clean up FCM service
   */
  async cleanup(): Promise<void> {
    await this.updateDeviceStatus(DEVICE_STATUS.INACTIVE);
    this.stopTokenRefresh();
    // Don't delete the token on cleanup, only on explicit logout
  }

  /**
   * Test push notification (for development)
   */
  async testNotification(): Promise<void> {
    if (!this.fcmToken) {
      return;
    }

    try {
      const response = await this.custom(
        {
          typeQL: "mutation",
          PATH_RESPONSE: FCM_RESPONSE_PATHS.TEST_PUSH_NOTIFICATION,
          variables: {
            token: this.fcmToken,
            title: 'Test Notification',
            body: 'This is a test push notification',
            data: {
              type: 'test',
              timestamp: new Date().toISOString()
            }
          }
        },
        FCM_MUTATIONS.TEST_PUSH_NOTIFICATION
      );

      if (response?.data?.success) {
        // Test notification sent successfully
      }
    } catch (error) {
      // Silent failure - failed to send test notification
    }
  }
}

// Export singleton instance
export default new FCMService();